import React from 'react';
import { Bus, Car, ShieldCheck, CreditCard, MapPin, ArrowRight, Wind, Users } from 'lucide-react';
import ServiceTrustStrip from './ServiceTrustStrip';
import {
  BUS_CARD_TRUST,
  PRIVATE_CARD_TRUST,
  HOME_HERO_TRUST,
} from '../serviceFeatures';
import SalePrice from './SalePrice';
import PopularRouteCards from './PopularRouteCards';
import { GUIDE_CONFIGS } from '../guides';

// Pure homepage body. PURITY: react, lucide-react, src/seo/* only.
//
// Props:
//   sale            — from resolveTransferSaleCampaign (null when inactive)
//   sharedEnabled   — whether shared-trip UI is shown
//   liveSlot        — runtime-only block (schedule + modal slot); null when prerendering
//   LinkComponent   — ({ href, className, children }) — plain <a> at build time

const DefaultLink = ({ href, className, children }) => (
  <a href={href} className={className}>
    {children}
  </a>
);

const heroRoad = '/images/home/hero-road-sunset.jpg';
const routeCanyon = '/images/home/route-canyon-sunset.jpg';
const interiorPassengers = '/images/home/interior-passengers.jpg';
const saleLandscape = '/images/home/transfer-sale-kodak-gold.webp';

const HomeContent = ({
  sale = null,
  sharedEnabled = false,
  liveSlot = null,
  LinkComponent = DefaultLink,
}) => {
  const Link = LinkComponent;

  return (
    <div className="bg-riverside-cream">
      {/* Hero — place-led, full-bleed regional photography */}
      <section className="relative min-h-[88vh] flex items-center">
        <img
          src={heroRoad}
          alt="A Riverside shuttle on the open highway between Nairobi and Arusha at golden hour, with acacia savannah and distant hills."
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Ink scrim for legibility over the photograph */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(45,30,23,0.82) 0%, rgba(45,30,23,0.55) 45%, rgba(45,30,23,0.15) 100%)',
          }}
        />
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-xs font-bold uppercase tracking-widest text-white ring-1 ring-white/25">
              <MapPin size={13} /> Nairobi · Arusha · Moshi
            </span>
            <h1 className="mt-5 font-serif font-bold text-white text-4xl md:text-6xl leading-[1.05]">
              Road transfers across
              <br className="hidden md:block" /> East Africa.
            </h1>
            <p className="mt-5 text-lg text-white/85 max-w-xl">
              Book a seat on a scheduled shuttle or charter a private vehicle between Nairobi,
              Arusha, and Moshi. Vetted drivers, comfortable vehicles, and prices you can see up
              front.
            </p>
            {sale && (
              <p className="mt-5 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white"><span className="rounded bg-[#A44432] px-2 py-1 font-bold">Transfer sale</span><span>{sale.deadlineShort}</span></p>
            )}
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link
                href="/bus"
                className="btn-primary px-8 py-4 text-lg"
              >
                <Bus size={20} /> Book a bus ticket
              </Link>
              <Link
                href="/private-transfer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium rounded-xl bg-white/10 text-white ring-1 ring-white/40 backdrop-blur-sm hover:bg-white/20 transition-colors active:scale-95"
              >
                <Car size={20} /> Get a private quote
              </Link>
              {sharedEnabled && (
                <Link
                  href="/trips"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium rounded-xl bg-white/10 text-white ring-1 ring-white/40 backdrop-blur-sm hover:bg-white/20 transition-colors active:scale-95"
                >
                  <Users size={20} /> Browse shared transfers
                </Link>
              )}
            </div>
            <ServiceTrustStrip
              features={HOME_HERO_TRUST}
              tone="light"
              className="mt-10"
            />
          </div>
        </div>
      </section>

      {/* Two launch products — clear, distinct jobs */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-6">
          <Link
            href="/bus"
            className="group relative overflow-hidden rounded-2xl border border-riverside-sand bg-white p-8 transition-colors hover:border-riverside-red"
          >
            <div className="w-12 h-12 rounded-xl bg-riverside-red/10 flex items-center justify-center text-riverside-red mb-5">
              <Bus size={24} />
            </div>
            <h2 className="font-serif text-2xl font-bold text-riverside-dark">Scheduled bus tickets</h2>
            <p className="mt-2 text-riverside-muted max-w-sm">
              Reserve a seat on our Nairobi to Arusha shuttle running twice daily at 08:00 and 14:00. Pick your date, seats, and hotel, and pay securely online.
            </p>
            <ServiceTrustStrip
              features={BUS_CARD_TRUST}
              size="sm"
              className="mt-4"
            />
            <span className="mt-5 inline-flex items-center gap-1.5 font-medium text-riverside-red">
              Find a departure
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </span>
          </Link>

          {sale ? (
            <Link href="/private-transfer" className="group overflow-hidden rounded-2xl border border-riverside-sand bg-white transition-colors hover:border-riverside-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-riverside-red">
              <img src={saleLandscape} alt="" width="1536" height="1024" loading="lazy" className="h-36 w-full object-cover object-[center_60%] md:h-44" />
              <div className="p-6 md:p-8">
                <h2 className="mb-4 text-sm font-semibold text-riverside-muted">Private transfer sale</h2>
                <SalePrice offer={sale.privateOffers[0]} />
                <span className="mt-5 flex items-center justify-between font-semibold text-riverside-red">Get a quote<ArrowRight size={18} aria-hidden="true" /></span>
              </div>
            </Link>
          ) : (
            <Link
              href="/private-transfer"
              className="group relative overflow-hidden rounded-2xl border border-riverside-sand bg-white p-8 transition-colors hover:border-riverside-red"
            >
              <div className="w-12 h-12 rounded-xl bg-riverside-red/10 flex items-center justify-center text-riverside-red mb-5">
                <Car size={24} />
              </div>
              <h2 className="font-serif text-2xl font-bold text-riverside-dark">Private transfers</h2>
              <p className="mt-2 text-riverside-muted max-w-sm">
                Charter a whole vehicle door to door, on your schedule. Enter any pickup and
                destination for an instant quote.
              </p>

              <ServiceTrustStrip
                features={PRIVATE_CARD_TRUST}
                size="sm"
                className="mt-4"
              />
              <span className="mt-5 inline-flex items-center gap-1.5 font-medium text-riverside-red">
                Get a quote
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          )}
        </div>
      </section>

      {liveSlot}

      {/* Popular routes — internal links for SEO + quick booking */}
      <section className={`max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-20${sharedEnabled ? ' pt-16 md:pt-20' : ''}`}>
        <h2 className="font-serif text-3xl font-bold text-riverside-dark">Popular routes</h2>
        <p className="mt-2 text-riverside-muted max-w-lg">
          The journeys our travellers book most often — jump straight to a route.
        </p>
        <p className="mt-2 text-sm">
          <Link href="/schedule" className="font-medium text-riverside-red hover:underline">
            View the full shuttle timetable
          </Link>
        </p>
        <div className="mt-8">
          <PopularRouteCards LinkComponent={Link} />
        </div>
      </section>

      {/* Route proof — make place the idea */}
      <section className="relative overflow-hidden">
        <img
          src={routeCanyon}
          alt="A shuttle descending the Great Rift Valley escarpment road at sunset."
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-riverside-dark/60" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-28">
          <div className="max-w-xl text-white">
            <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight">
              The routes we know best
            </h2>
            <p className="mt-4 text-white/85">
              From the Nairobi highway to the Rift Valley escarpment and on to Arusha and Moshi —
              our drivers run these roads every week. You get the comfortable, reliable trip you
              booked, at the price you were quoted.
            </p>
          </div>
        </div>
      </section>

      {/* Why Riverside — open editorial layout, no card chrome */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-[1fr,1.1fr] gap-10 lg:gap-16 items-center">
          <div>
            <h2 className="font-serif text-3xl font-bold text-riverside-dark">
              Comfortable, dependable, transparent
            </h2>
            <p className="mt-3 text-riverside-muted max-w-lg">
              Every Riverside journey is run by a vetted driver in a well-kept vehicle, with pricing
              agreed before you travel.
            </p>
            <dl className="mt-8 space-y-6">
              {[
                {
                  icon: ShieldCheck,
                  title: 'Vetted local drivers',
                  desc: 'Experienced drivers who run these routes regularly and know the roads and border crossings.',
                },
                {
                  icon: CreditCard,
                  title: 'Transparent, upfront pricing',
                  desc: 'See the full price before you pay. Secure card payment with no surprises on the day.',
                },
                {
                  icon: Wind,
                  title: 'Air-conditioned vehicles',
                  desc: 'Clean, maintained shuttles and private vehicles with AC, built for long East African road trips.',
                },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-riverside-red/10 flex items-center justify-center text-riverside-red">
                    <Icon size={22} />
                  </div>
                  <div>
                    <dt className="font-bold text-riverside-dark">{title}</dt>
                    <dd className="mt-1 text-sm text-riverside-muted">{desc}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
          <div className="rounded-2xl overflow-hidden border border-riverside-sand">
            <img
              src={interiorPassengers}
              alt="Passengers relaxing aboard a Riverside shuttle, looking out over the Rift Valley."
              className="w-full h-full object-cover aspect-[4/3]"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Final conversion */}
      <section className="bg-riverside-dark text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center">
          <h2 className="font-serif text-3xl font-bold">Ready when you are</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">
            Book a bus seat in minutes, get an instant private transfer quote
            {sharedEnabled ? ', or join a shared transfer.' : '.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link href="/bus" className="btn-primary px-8 py-4 text-lg">
              <Bus size={20} /> Book a bus ticket
            </Link>
            <Link
              href="/private-transfer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium rounded-xl bg-white/10 text-white ring-1 ring-white/40 hover:bg-white/20 transition-colors active:scale-95"
            >
              <Car size={20} /> Get a private quote
            </Link>
            {sharedEnabled && (
              <Link
                href="/trips"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium rounded-xl bg-white/10 text-white ring-1 ring-white/40 hover:bg-white/20 transition-colors active:scale-95"
              >
                <Users size={20} /> Browse shared transfers
              </Link>
            )}
          </div>
          <nav
            aria-label="Plan your trip"
            className="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-white/75"
          >
            <Link href="/offices" className="hover:text-white hover:underline">
              Offices &amp; contact
            </Link>
            <Link href="/booking-conditions" className="hover:text-white hover:underline">
              Booking conditions
            </Link>
            {GUIDE_CONFIGS.map((guide) => (
              <Link
                key={guide.slug}
                href={`/${guide.slug}`}
                className="hover:text-white hover:underline"
              >
                {guide.breadcrumbLabel}
              </Link>
            ))}
          </nav>
        </div>
      </section>
    </div>
  );
};

export default HomeContent;
